import { Injectable, signal, computed, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NavigationItem } from '../types/navigation.interface';

@Injectable({
  providedIn: 'root'
})
export class MenuStateService {
  private readonly router = inject(Router);

  private readonly flatNavigation = signal<NavigationItem[]>([]);
  private readonly activeRouteIds = signal<Set<string>>(new Set());
  private readonly openMenuIds = signal<Set<string>>(new Set());

  constructor() {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.updateStateFromUrl(event.urlAfterRedirects);
    });
  }

  initialize(navItems: NavigationItem[]): void {
    const flatList = this.flattenNavigation(navItems);
    this.flatNavigation.set(flatList);
    this.updateStateFromUrl(this.router.url);
  }

  isRouteActive = computed(() => (menuId: string) => this.activeRouteIds().has(menuId));
  isMenuOpen = computed(() => (menuId: string) => this.openMenuIds().has(menuId));

  toggleMenu(menuId: string): void {
    const menuToToggle = this.flatNavigation().find(m => m.id === menuId);
    if (!menuToToggle || !menuToToggle.children?.length) {
      return;
    }

    this.openMenuIds.update(currentOpenIds => {
      const newOpenIds = new Set(currentOpenIds);
      const isAlreadyOpen = newOpenIds.has(menuId);

      if (isAlreadyOpen) {
        newOpenIds.delete(menuId);
        this.closeChildren(menuId, newOpenIds);
      } else {
        const parentId = menuToToggle.parentId;
        const siblings = this.flatNavigation().filter(
          item => item.parentId === parentId && item.id !== menuId
        );

        for (const sibling of siblings) {
          if (newOpenIds.has(sibling.id)) {
            newOpenIds.delete(sibling.id);
            this.closeChildren(sibling.id, newOpenIds);
          }
        }

        newOpenIds.add(menuId);
      }

      return newOpenIds;
    });
  }

  private updateStateFromUrl(url: string): void {
    const allMenus = this.flatNavigation();
    const matchingMenu = allMenus.find(menu => menu.url && url.startsWith(menu.url));
    const newActiveIds = new Set<string>();

    if (matchingMenu) {
      newActiveIds.add(matchingMenu.id);
      this.addParentIds(matchingMenu, newActiveIds);
    }

    this.activeRouteIds.set(newActiveIds);

    this.openMenuIds.update(currentOpenIds => {
      return new Set([...currentOpenIds, ...newActiveIds]);
    });
  }

  private addParentIds(menu: NavigationItem, idSet: Set<string>): void {
    if (menu.parentId) {
      const parent = this.flatNavigation().find(p => p.id === menu.parentId);
      if (parent) {
        idSet.add(parent.id);
        this.addParentIds(parent, idSet);
      }
    }
  }

  private closeChildren(parentId: string, idSet: Set<string>): void {
    const children = this.flatNavigation().filter(item => item.parentId === parentId);
    for (const child of children) {
      if (idSet.has(child.id)) {
        idSet.delete(child.id);
        this.closeChildren(child.id, idSet);
      }
    }
  }

  private flattenNavigation(items: NavigationItem[], parentId?: string): NavigationItem[] {
    return items.reduce<NavigationItem[]>((acc, item) => {
      const flatItem = { ...item, parentId };
      acc.push(flatItem);
      if (item.children) {
        acc.push(...this.flattenNavigation(item.children, item.id));
      }
      return acc;
    }, []);
  }
}
